import { useCallback, useMemo } from "react";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import {
  BellRing,
  CalendarClock,
  FolderCheck,
  Truck,
  type LucideIcon,
} from "lucide-react";
import type { ReservationRequest } from "@/v2/shared/model";
import { daysUntil, formatDate } from "@/v2/shared/format";
import { findAsset } from "@/v2/data/db";
import { useCurrentUser } from "@/v2/auth/useCurrentUser";
import { useRequests } from "@/v2/data/queries";

// Meldingen (PZAO-41). Conditie-gestuurd: berekend uit de actuele data, een
// melding verdwijnt automatisch zodra de situatie opgelost is (taak afgevinkt,
// verlengd, afgesloten, ophaling geregistreerd). Elke melding heeft een
// doelgroep: gericht aan één beheerdersaccount of aan het hele team — de
// lijst is per gebruiker (beslist 12/08). Triggers: taakherinneringen
// (PZAO-26) en vervalsignalen (PZAO-39).

// Zelfde vensters als het dashboard (PZAO-30/40): einddatum dossier binnen
// een week, plaatsing/ophaling binnen 2 dagen.
const EXPIRY_WINDOW_DAYS = 7;
const PICKUP_WINDOW_DAYS = 2;

export type NotificationKind =
  | "taskReminder"
  | "dossierExpiry"
  | "pickupDue"
  | "closePending";

export type V2Notification = {
  key: string;
  kind: NotificationKind;
  title: string;
  context: string;
  timing: string;
  to: string;
  days: number;
  // undefined = teammelding, zichtbaar voor alle beheerders.
  targetUserId?: string;
  // Enkel bij taakherinneringen: actuele status van de taak. Een afgewerkte
  // taak blijft als melding staan tot ze bekeken is, en verdwijnt daarna.
  taskDone?: boolean;
};

export const NOTIFICATION_KIND_META: Record<
  NotificationKind,
  { icon: LucideIcon; label: string }
> = {
  taskReminder: { icon: BellRing, label: "Taakherinnering" },
  dossierExpiry: { icon: CalendarClock, label: "Einddatum dossier" },
  pickupDue: { icon: Truck, label: "Ophaling" },
  closePending: { icon: FolderCheck, label: "Dossier afronden" },
};

export function relativeDays(days: number): string {
  if (days < 0) return `${-days} ${-days === 1 ? "dag" : "dagen"} geleden`;
  if (days === 0) return "vandaag";
  if (days === 1) return "morgen";
  return `over ${days} dagen`;
}

// Ernst volgt uit de dagen: verstreken = error, vandaag = warning, komend = info.
export function notificationIconTone(days: number): string {
  if (days < 0) return "bg-error-50 text-error-600";
  if (days === 0) return "bg-warning-50 text-warning-600";
  return "bg-info-50 text-info-600";
}

export function notificationTextTone(days: number): string {
  if (days < 0) return "text-error-600";
  if (days === 0) return "text-warning-700";
  return "text-info-700";
}

export function notificationBadgeColor(days: number): "error" | "warning" | "info" {
  if (days < 0) return "error";
  if (days === 0) return "warning";
  return "info";
}

function collect(requests: ReadonlyArray<ReservationRequest>): Array<V2Notification> {
  const items: Array<V2Notification> = [];

  for (const r of requests) {
    if (r.archived || r.status === "CLOSED") continue;
    const context = `${r.number} · ${r.title}`;

    // PZAO-26: taakherinneringen waarvan de datum bereikt of verstreken is.
    // Gericht aan de beheerder die op de herinnering gekozen is; zonder
    // persoon geldt de melding voor het hele team. Afgevinkte taken blijven
    // als melding staan (status "Afgewerkt") tot ze bekeken zijn; dat filteren
    // gebeurt in useNotifications, waar de bekeken-status gekend is.
    const addReminders = (
      tasks: ReadonlyArray<{
        id: string;
        label: string;
        done: boolean;
        reminderDate?: string;
        reminderTime?: string;
        reminderAssigneeId?: string;
      }>,
      to: string,
    ): void => {
      for (const task of tasks) {
        if (!task.reminderDate) continue;
        const days = daysUntil(task.reminderDate);
        if (days > 0) continue;
        items.push({
          key: `reminder-${task.id}`,
          kind: "taskReminder",
          title: task.label,
          context,
          timing: `Herinnering op ${formatDate(task.reminderDate)}${task.reminderTime ? ` om ${task.reminderTime}` : ""}`,
          to,
          days,
          targetUserId: task.reminderAssigneeId,
          taskDone: task.done,
        });
      }
    };

    addReminders(r.tasks, `/aanvragen/${r.id}`);

    for (const d of r.dossiers) {
      if (d.status === "CLOSED") continue;
      const dossierTo = `/dossiers/${d.id}`;
      addReminders(d.tasks, dossierTo);
      for (const dep of d.deployments) addReminders(dep.tasks, dossierTo);

      // PZAO-39: alle inzetten afgerond maar het dossier nog niet afgesloten
      // (afronden vergeten). Vervangt het gewone einddatum-signaal.
      const allClosed =
        d.deployments.length > 0 && d.deployments.every((dep) => dep.status === "CLOSED");
      if (allClosed) {
        const lastEnd = d.deployments
          .map((dep) => dep.actualEnd ?? dep.expectedEnd)
          .sort()
          .at(-1);
        const days = lastEnd ? daysUntil(lastEnd) : 0;
        items.push({
          key: `close-${d.id}`,
          kind: "closePending",
          title: "Alle inzetten afgerond, dossier nog niet afgesloten",
          context,
          timing: lastEnd
            ? `Laatste inzet afgerond op ${formatDate(lastEnd)}`
            : "Laatste inzet afgerond",
          to: dossierTo,
          days,
        });
        continue;
      }

      // PZAO-39: toegekende einddatum van een ingepland dossier nadert of is
      // overschreden (verlengen, ophaling regelen of afsluiten).
      if (d.status === "PLANNED") {
        const days = daysUntil(d.endDate);
        if (days <= EXPIRY_WINDOW_DAYS) {
          items.push({
            key: `expiry-${d.id}`,
            kind: "dossierExpiry",
            title: days < 0 ? "Einddatum van het dossier overschreden" : "Dossier nadert de einddatum",
            context,
            timing: `Toegekend tot ${formatDate(d.endDate)}`,
            to: dossierTo,
            days,
          });
        }
      }

      // PZAO-39: geplaatste inzet waarvan het verwachte einde binnen 2 dagen
      // valt of voorbij is, zonder geregistreerde ophaling.
      for (const dep of d.deployments) {
        if (dep.status !== "DEPLOYED") continue;
        const days = daysUntil(dep.expectedEnd);
        if (days > PICKUP_WINDOW_DAYS) continue;
        const assetNumber = findAsset(dep.assetId)?.number ?? "-";
        items.push({
          key: `pickup-${dep.id}`,
          kind: "pickupDue",
          title: `Ophaling regelen voor ${assetNumber}`,
          context,
          timing: `Verwacht einde op ${formatDate(dep.expectedEnd)}`,
          to: dossierTo,
          days,
        });
      }
    }
  }

  // Meest urgente eerst (meest overschreden bovenaan).
  return items.sort((a, b) => a.days - b.days);
}

// Bekeken-status (PZAO-41): strikt per gebruiker, ook voor teammeldingen —
// elke beheerder heeft zijn eigen gelezen-status. "Bekeken" betekent enkel
// gezien (badge en nieuw-indicator), nooit afgehandeld: de melding blijft in
// de lijst staan tot de onderliggende situatie opgelost is (conditie-
// gestuurd). Een melding geldt als bekeken zodra ze effectief in beeld is
// geweest (belpaneel geopend of meldingenpagina bezocht).
type SeenByUser = Record<string, ReadonlyArray<string>>;

const seenNotificationsAtom = atomWithStorage<SeenByUser>(
  "pza.v2.seenNotifications",
  {},
  undefined,
  { getOnInit: true },
);

// Meldingen voor de ingelogde gebruiker, als één lijst gesorteerd op urgentie
// (gangbaar inbox-patroon: gericht en team gemengd; persoonlijke relevantie is
// een indicator per melding, geen aparte sectie). `isPersonal` markeert de
// meldingen die specifiek aan de ingelogde gebruiker gericht zijn; `isUnread`
// de meldingen die deze gebruiker nog niet in beeld heeft gehad.
export function useNotifications(): {
  items: Array<V2Notification & { isPersonal: boolean; isUnread: boolean }>;
  total: number;
  unreadCount: number;
  markSeen: (keys: ReadonlyArray<string>) => void;
} {
  const user = useCurrentUser();
  const { data } = useRequests();
  const [seenByUser, setSeenByUser] = useAtom(seenNotificationsAtom);

  const markSeen = useCallback(
    (keys: ReadonlyArray<string>): void => {
      if (keys.length === 0) return;
      setSeenByUser((prev) => {
        const current = new Set(prev[user.id] ?? []);
        const before = current.size;
        for (const key of keys) current.add(key);
        if (current.size === before) return prev;
        return { ...prev, [user.id]: [...current] };
      });
    },
    [setSeenByUser, user.id],
  );

  return useMemo(() => {
    const seen = new Set(seenByUser[user.id] ?? []);
    const items = collect(data ?? [])
      .filter((n) => n.targetUserId === undefined || n.targetUserId === user.id)
      .map((n) => ({
        ...n,
        isPersonal: n.targetUserId === user.id,
        isUnread: !seen.has(n.key),
      }))
      // Een herinnering op een afgewerkte taak blijft staan tot ze bekeken is
      // (zo ziet de gerichte beheerder dat de taak intussen opgelost is).
      .filter((n) => !n.taskDone || n.isUnread);
    return {
      items,
      total: items.length,
      unreadCount: items.filter((n) => n.isUnread).length,
      markSeen,
    };
  }, [data, user.id, seenByUser, markSeen]);
}
